import { motion } from "framer-motion";
import { MapPin, Clock, MessageCircle, Instagram, ArrowUp } from "lucide-react";

export const Footer = ({ company }) => {
  const wa = company?.wa_number || "6285714757850";
  const ig = company?.instagram || "https://www.instagram.com/_planet_gudang_?stkn=amE0ZWVmMG42emxo";
  const waLink = `https://wa.me/${wa}?text=${encodeURIComponent("Halo, saya ingin bertanya tentang layanan Planet Gudang")}`;

  return (
    <footer id="kontak" data-testid="footer-section" className="relative overflow-hidden border-t border-[#28303f] bg-[#07090e]">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-mono-tech mb-6 text-xs uppercase tracking-[0.35em] text-amber-400">Hubungi Kami</p>
            <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl lg:text-6xl" data-testid="footer-headline">
              Siap Meluncur<br />
              <span className="text-stroke">Bersama Kami?</span>
            </h2>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-wa-button"
                className="flex items-center justify-center gap-3 bg-[#22c55e] px-8 py-5 font-mono-tech text-xs uppercase tracking-[0.25em] text-[#04120a] transition-all duration-300 hover:-translate-y-1 hover:bg-[#16a34a]"
              >
                <MessageCircle className="h-4 w-4" />
                Chat WhatsApp
              </a>
              <a
                href={ig}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-ig-button"
                className="flex items-center justify-center gap-3 border border-[#e1306c]/60 px-8 py-5 font-mono-tech text-xs uppercase tracking-[0.25em] text-[#f472a5] transition-all duration-300 hover:-translate-y-1 hover:bg-[#e1306c] hover:text-white"
              >
                <Instagram className="h-4 w-4" />
                Instagram Kami
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="space-y-8 lg:pl-12"
          >
            <div className="flex gap-4" data-testid="footer-address">
              <MapPin className="mt-1 h-5 w-5 shrink-0 text-amber-400" />
              <div>
                <p className="font-mono-tech mb-1 text-[10px] uppercase tracking-[0.3em] text-slate-500">Alamat</p>
                <p className="text-sm leading-relaxed text-slate-300">{company?.address || "Bekasi Jaya Indah Danita, Jl. Anggrek 7 Blok E9 No.10, RT 005 RW 013, Bekasi"}</p>
              </div>
            </div>
            <div className="flex gap-4" data-testid="footer-phone">
              <MessageCircle className="mt-1 h-5 w-5 shrink-0 text-amber-400" />
              <div>
                <p className="font-mono-tech mb-1 text-[10px] uppercase tracking-[0.3em] text-slate-500">Telepon / WhatsApp</p>
                <p className="text-sm text-slate-300">{company?.phone_display || "0857-1475-7850"}</p>
              </div>
            </div>
            <div className="flex gap-4" data-testid="footer-hours">
              <Clock className="mt-1 h-5 w-5 shrink-0 text-amber-400" />
              <div>
                <p className="font-mono-tech mb-1 text-[10px] uppercase tracking-[0.3em] text-slate-500">Jam Operasional</p>
                <p className="text-sm text-slate-300">{company?.hours || "Senin - Sabtu, 08.00 - 17.00 WIB"}</p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16"
          data-testid="footer-map-section"
        >
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-mono-tech mb-2 text-[10px] uppercase tracking-[0.3em] text-amber-400">Koordinat Kantor</p>
              <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-slate-100">Temukan Kami di Bekasi</h3>
            </div>
            <a
              href="https://maps.app.goo.gl/pwJUvrC25wdaF9aZ8"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-map-open-button"
              className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-slate-400 underline-offset-4 transition-colors hover:text-amber-400 hover:underline"
            >
              Buka di Google Maps
            </a>
          </div>
          <div className="img-clip relative h-[320px] w-full overflow-hidden border border-[#28303f] sm:h-[400px]">
            <iframe
              title="Peta Lokasi PT Planet Gudang Antar Galaksi"
              data-testid="footer-map-iframe"
              src="https://www.google.com/maps?q=-6.2275316,107.0219686+(PT+Planet+Gudang+Antar+Galaksi)&z=18&output=embed"
              className="h-full w-full grayscale-[0.35] contrast-[1.05]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <span className="pointer-events-none absolute left-4 top-4 bg-[#07090e]/85 px-3 py-1.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-amber-400 backdrop-blur-sm">
              -6.2275° S / 107.0220° E
            </span>
          </div>
        </motion.div>

        <div className="mt-24 flex flex-col items-start justify-between gap-6 border-t border-[#28303f] pt-8 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.webp" alt="Logo Planet Gudang" className="h-9 w-9 rounded-full border border-[#28303f] object-cover" />
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500" data-testid="footer-copyright">
              © 2026 PT Planet Gudang Antar Galaksi
            </p>
          </div>
          <a
            href="#beranda"
            data-testid="footer-back-to-top"
            className="group flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 transition-colors hover:text-amber-400"
          >
            Kembali ke Orbit
            <ArrowUp className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1" />
          </a>
        </div>
      </div>
    </footer>
  );
};
